import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, Shield } from 'lucide-react';

export interface ComplianceItem {
  id: string;
  label: string;
  required: boolean;
  response?: 'yes' | 'no' | 'na';
}

export interface ComplianceChecklistData {
  items: Record<string, 'yes' | 'no' | 'na'>;
  specificFeedback?: string;
  proficiencyLevel?: 'independent' | 'moderate' | 'intensive';
}

interface SupervisorComplianceChecklistProps {
  onComplete: (data: ComplianceChecklistData) => void;
  initialData?: ComplianceChecklistData;
}

export const SupervisorComplianceChecklist: React.FC<SupervisorComplianceChecklistProps> = ({
  onComplete,
  initialData,
}) => {
  const checklistItems: ComplianceItem[] = [
    {
      id: 'bacb_ethics',
      label: 'Demonstrates compliance with BACB RBT Ethics Code',
      required: true,
    },
    {
      id: 'parent_guardian',
      label: 'Checks in with Parent/Guardian, maintains materials and transitions appropriately',
      required: true,
    },
    {
      id: 'client_pairing',
      label: 'Pairs with client, uses reinforcement and breaks appropriately',
      required: true,
    },
    {
      id: 'interventions',
      label: 'Provides interventions, including error correction, as per program instructions',
      required: true,
    },
    {
      id: 'data_collection',
      label: 'Collects data accurately',
      required: true,
    },
  ];

  const [responses, setResponses] = useState<Record<string, 'yes' | 'no' | 'na'>>(
    initialData?.items || {}
  );
  const [specificFeedback, setSpecificFeedback] = useState(
    initialData?.specificFeedback || ''
  );
  const [proficiencyLevel, setProficiencyLevel] = useState<
    'independent' | 'moderate' | 'intensive'
  >(initialData?.proficiencyLevel || 'independent');
  const [showWarning, setShowWarning] = useState(false);

  const handleResponseChange = (itemId: string, response: 'yes' | 'no' | 'na') => {
    setResponses((prev) => ({
      ...prev,
      [itemId]: response,
    }));
    setShowWarning(false);
  };

  const handleSubmit = () => {
    // Check if all required items have responses
    const missingResponses = checklistItems
      .filter((item) => item.required && !responses[item.id])
      .map((item) => item.label);

    if (missingResponses.length > 0) {
      setShowWarning(true);
      return;
    }

    // Check if any items marked "No"
    const noResponses = Object.entries(responses).filter(([_, value]) => value === 'no');

    if (noResponses.length > 0 && !specificFeedback.trim()) {
      setShowWarning(true);
      return;
    }

    onComplete({
      items: responses,
      specificFeedback: specificFeedback.trim() || undefined,
      proficiencyLevel,
    });
  };

  const allRequiredCompleted = checklistItems
    .filter((item) => item.required)
    .every((item) => responses[item.id]);

  const hasNoResponses = Object.values(responses).some((value) => value === 'no');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-bold text-blue-900">
              General Session Protocol Compliance
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Evaluate RBT performance during this supervision session
            </p>
          </div>
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-4">
        {checklistItems.map((item) => (
          <div
            key={item.id}
            className={`border-2 rounded-lg p-4 ${
              responses[item.id] === 'yes'
                ? 'border-green-300 bg-green-50'
                : responses[item.id] === 'no'
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{item.label}</p>
                {item.required && (
                  <p className="text-xs text-gray-600 mt-1">* Required</p>
                )}
              </div>

              {/* Response Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleResponseChange(item.id, 'yes')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    responses[item.id] === 'yes'
                      ? 'bg-green-600 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-green-100 hover:text-green-700'
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleResponseChange(item.id, 'no')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    responses[item.id] === 'no'
                      ? 'bg-red-600 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-red-100 hover:text-red-700'
                  }`}
                >
                  No
                </button>
                <button
                  onClick={() => handleResponseChange(item.id, 'na')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    responses[item.id] === 'na'
                      ? 'bg-gray-600 text-white shadow-md scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Specific Feedback (Required if any "No" responses) */}
      {hasNoResponses && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-bold text-yellow-900">
              Specific Feedback Required
            </p>
          </div>
          <p className="text-xs text-yellow-700 mb-3">
            Describe specific areas per items checked off above. For example, "Provided
            guidance and training regarding reinforcement and prompting implementation",
            "Provided guidance and training regarding data collection". If an Adaptive
            Behavior Treatment with Protocol Modification was completed, note if...
          </p>
          <textarea
            value={specificFeedback}
            onChange={(e) => setSpecificFeedback(e.target.value)}
            placeholder="Enter specific feedback for technician..."
            className="w-full p-3 border-2 border-yellow-300 rounded-lg text-sm min-h-32 focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
        </div>
      )}

      {/* Proficiency Level */}
      <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
        <label className="block text-sm font-bold text-gray-900 mb-3">
          Technician listed above is{' '}
          <span className="text-blue-600">Level of Proficiency</span> in implementing
          ABA strategies and techniques:
        </label>
        <div className="flex gap-3">
          <button
            onClick={() => setProficiencyLevel('independent')}
            className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
              proficiencyLevel === 'independent'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-green-500'
            }`}
          >
            Independent
          </button>
          <button
            onClick={() => setProficiencyLevel('moderate')}
            className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
              proficiencyLevel === 'moderate'
                ? 'bg-yellow-600 text-white shadow-md'
                : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-yellow-500'
            }`}
          >
            Moderate Support
          </button>
          <button
            onClick={() => setProficiencyLevel('intensive')}
            className={`flex-1 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
              proficiencyLevel === 'intensive'
                ? 'bg-red-600 text-white shadow-md'
                : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-red-500'
            }`}
          >
            Intensive Support
          </button>
        </div>
      </div>

      {/* Warning Message */}
      {showWarning && (
        <div className="bg-red-100 border-2 border-red-400 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-red-900 mb-1">
                Cannot Submit - Missing Required Information
              </p>
              <ul className="text-xs text-red-700 space-y-1">
                {!allRequiredCompleted && (
                  <li>• Please respond to all required checklist items</li>
                )}
                {hasNoResponses && !specificFeedback.trim() && (
                  <li>
                    • Specific feedback is required when any items are marked "No"
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        className={`w-full px-6 py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-3 ${
          allRequiredCompleted && (!hasNoResponses || specificFeedback.trim())
            ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg active:scale-95'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        <CheckCircle className="w-6 h-6" />
        Complete Compliance Check
      </button>

      {/* Info */}
      <div className="text-xs text-gray-600 space-y-1 bg-gray-50 rounded-lg p-3">
        <p>• All required items must be marked before submission</p>
        <p>• Items marked "No" require specific feedback and guidance</p>
        <p>• This checklist ensures compliance with BACB supervision requirements</p>
      </div>
    </div>
  );
};
